import { HttpClient, HttpParams } from "@angular/common/http";
import { Inject } from "@angular/core";
import { Observable } from "rxjs";
import { BaseFilterModel } from "../models/base-filter.model";
import { BaseItemModel } from "../models/base-item.model";
import { BaseListModel } from "../models/base-list.model";
import { HttpEncoderService } from "./http-encoder.service";
import { BaseDtoModel } from "@webapp/core/abstracts/models/base-dto.model";
import { AppConfigService } from "@webapp/core/app-config/services/app-config.service";
import { ApiVersionModel } from "@webapp/core/abstracts/models/api-version.model";
import { HttpActions } from "@webapp/core/abstracts/enums/http-actions.enum";

export abstract class BaseApiService<ItemModel extends BaseItemModel, DTOModel extends BaseDtoModel> {
  protected constructor(
    @Inject(String) entityPath: string,
    @Inject(ApiVersionModel) apiVersion: ApiVersionModel,
    httpClient: HttpClient,
    protected appConfigService: AppConfigService
  ) {
    this.httpClient = httpClient;
    this.entityPath = entityPath;
    this.apiVersion = apiVersion;
  }
  protected entityPath: string;
  protected apiVersion: ApiVersionModel;
  protected httpClient: HttpClient;

  public prepareFilters(filteringData: BaseFilterModel): HttpParams {
    // using string filters gives us better control on how the parameters are applied compared to object filters
    let stringFilters = "";
    const ipp = filteringData.limit ? filteringData.limit.toFixed(0) : "15";
    stringFilters += `itemsPerPage=${ipp}`;

    const page = filteringData.page ? filteringData.page.toFixed(0) : "1";
    stringFilters += `&page=${page}`;

    stringFilters += `&filter=${JSON.stringify(filteringData.filter)}`;

    stringFilters += `&fields=${filteringData.fields.join(",")}`;

    stringFilters += `&skip=${filteringData.skip}`;

    stringFilters += `&sort=${filteringData.sort}`;

    return new HttpParams({
      fromString: stringFilters,
      encoder: new HttpEncoderService(),
    });
  }
  protected getBasePath(httpAction: HttpActions): string {
    return `${this.appConfigService.get().endpoints.secure ? "https://" : "http://"}${this.appConfigService.get().endpoints.apiEndpoint}/${this.apiVersion[httpAction]}/${
      this.entityPath
    }`;
  }

  getAll$(filters?: BaseFilterModel): Observable<BaseListModel<ItemModel>> {
    if (!filters) {
      filters = new BaseFilterModel();
    }

    return this.httpClient.get<BaseListModel<ItemModel>>(this.getBasePath(HttpActions.getAll), {
      params: this.prepareFilters(filters),
    });
  }

  get$(id: unknown): Observable<ItemModel> {
    return this.httpClient.get<ItemModel>(`${this.getBasePath(HttpActions.get)}/${id}`);
  }

  post$(model: Partial<ItemModel> | Partial<DTOModel>): Observable<ItemModel> {
    return this.httpClient.post<ItemModel>(`${this.getBasePath(HttpActions.post)}/`, {
      ...model,
    });
  }

  put$(id: unknown, model: Partial<ItemModel> | Partial<DTOModel>): Observable<ItemModel> {
    return this.httpClient.put<ItemModel>(`${this.getBasePath(HttpActions.put)}/${id}`, {
      ...model,
    });
  }

  patch$(id: unknown, model: Partial<ItemModel> | Partial<DTOModel>): Observable<ItemModel> {
    return this.httpClient.patch<ItemModel>(`${this.getBasePath(HttpActions.patch)}/${id}`, {
      ...model,
    });
  }
  delete$(id: unknown): Observable<void> {
    return this.httpClient.delete<void>(`${this.getBasePath(HttpActions.delete)}/${id}`);
  }
}
